import { getPeriodBounds } from "./employeeDashboardMetrics";

const ABSENT_CODES = new Set(["LOP", "LWP", "AB", "ABSENT"]);
const LEAVE_EXCLUDE = new Set(["PR", "PH", "WO", "LOP", "LWP", "AB", "ABSENT", "-", ""]);

function getDeptName(employee) {
  return (
    employee?.department?.name ||
    employee?.departmentName ||
    employee?.Department?.name ||
    "Unassigned"
  );
}

function getEmployeeDisplayName(employee) {
  const user = employee?.user || {};
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return (
    employee?.name ||
    employee?.employeeName ||
    employee?.empCode ||
    `Employee ${employee?.id ?? ""}`
  );
}

function normalizeStatus(raw, reverseMapping) {
  if (raw == null || raw === "" || raw === "-") return null;
  let status = String(raw).trim().toUpperCase();
  if (reverseMapping && reverseMapping[status]) {
    status = String(reverseMapping[status]).trim().toUpperCase();
  }
  return status || null;
}

function isLeaveCode(code, leaveTypeCodes) {
  if (!code || LEAVE_EXCLUDE.has(code)) return false;
  if (leaveTypeCodes && leaveTypeCodes.size > 0) return leaveTypeCodes.has(code);
  return code.length >= 2 && code.length <= 4;
}

/**
 * Aggregate attendance analytics for the admin dashboard.
 */
export function computeAttendanceDashboardMetrics({
  employees = [],
  attendanceData = {},
  leaveTypes = [],
  insightItems = [],
  attendanceDetailData = {},
  selectedMonth = 0,
  selectedYear = new Date().getFullYear(),
  reverseMapping = {},
  periodMode = "month",
  month = null,
  year = null,
  rangeFrom = "",
  rangeTo = "",
}) {
  const rows = Array.isArray(employees) ? employees : [];
  const effectiveMonth1 =
    periodMode === "month" && Number.isFinite(Number(month))
      ? Number(month)
      : selectedMonth + 1;
  const effectiveYear =
    Number.isFinite(Number(year)) && Number(year) > 0
      ? Number(year)
      : selectedYear;
  const month0 = effectiveMonth1 - 1;

  const { label: periodLabel } = getPeriodBounds(periodMode, {
    month: effectiveMonth1,
    year: effectiveYear,
    rangeFrom,
    rangeTo,
  });

  const daysInMonth = new Date(effectiveYear, month0 + 1, 0).getDate();
  const monthLabel =
    periodMode === "year"
      ? `Year ${effectiveYear}`
      : new Date(effectiveYear, month0, 1).toLocaleString("default", {
          month: "long",
          year: "numeric",
        });

  const leaveTypeCodes = new Set(
    (leaveTypes || [])
      .map((lt) =>
        String(lt?.code || lt?.leaveCode || lt?.shortCode || "")
          .trim()
          .toUpperCase()
      )
      .filter(Boolean)
  );

  const leaveBreakdown = {};
  const deptAbsent = {};
  const deptRegularization = {};
  const deptPresent = {};
  let totalPresentDays = 0;
  let totalAbsentDays = 0;
  let totalLeaveDays = 0;
  let totalWoDays = 0;
  let totalPhDays = 0;
  let employeesWithData = 0;
  const employeeScores = [];

  for (const emp of rows) {
    const empId = emp?.id;
    if (empId == null) continue;
    const map =
      attendanceData[empId] ||
      attendanceData[String(empId)] ||
      attendanceData[emp?.empCode] ||
      {};
    if (!map || typeof map !== "object") continue;

    const dept = getDeptName(emp);
    if (!deptAbsent[dept]) deptAbsent[dept] = 0;
    if (!deptRegularization[dept]) deptRegularization[dept] = 0;
    if (!deptPresent[dept]) deptPresent[dept] = 0;

    let present = 0;
    let absent = 0;
    let leave = 0;
    let wo = 0;
    let ph = 0;
    let hasAny = false;

    for (let day = 1; day <= daysInMonth; day++) {
      const raw = map[day] ?? map[String(day)];
      const status = normalizeStatus(raw, reverseMapping);
      if (!status) continue;
      hasAny = true;

      if (status === "PR") {
        totalPresentDays += 1;
        present += 1;
        deptPresent[dept] += 1;
      }
      if (status === "WO") {
        totalWoDays += 1;
        wo += 1;
      }
      if (status === "PH") {
        totalPhDays += 1;
        ph += 1;
      }
      if (ABSENT_CODES.has(status)) {
        totalAbsentDays += 1;
        absent += 1;
        deptAbsent[dept] += 1;
      }
      if (isLeaveCode(status, leaveTypeCodes)) {
        totalLeaveDays += 1;
        leave += 1;
        leaveBreakdown[status] = (leaveBreakdown[status] || 0) + 1;
      }
    }

    if (hasAny) employeesWithData += 1;

    const tracked = present + absent + leave;
    const attendanceRate =
      tracked > 0 ? Math.round((present / tracked) * 1000) / 10 : 0;
    employeeScores.push({
      id: empId,
      name: getEmployeeDisplayName(emp),
      empCode: emp?.empCode || "",
      presentDays: present,
      absentDays: absent,
      leaveDays: leave,
      woDays: wo,
      phDays: ph,
      attendanceRate,
      count: `${present}/${daysInMonth}`,
    });

    const detail =
      attendanceDetailData[empId] ||
      attendanceDetailData[String(empId)] ||
      {};
    const regCount = Object.values(detail || {}).filter(
      (d) => d && (d.checkIn || d.checkOut) && d.checkIn !== "--"
    ).length;
    if (regCount > 0) deptRegularization[dept] += regCount;
  }

  const insights = Array.isArray(insightItems) ? insightItems : [];
  let pendingLeaveRequests = 0;
  let approvedLeaveRequests = 0;
  let rejectedLeaveRequests = 0;
  let regularizationInsights = 0;

  for (const item of insights) {
    const action = String(item?.action || "").toLowerCase();
    const status = String(item?.status || "").toLowerCase();
    if (action.includes("leave")) {
      if (status === "pending") pendingLeaveRequests += 1;
      if (status === "approved") approvedLeaveRequests += 1;
      if (status === "rejected") rejectedLeaveRequests += 1;
    }
    if (action.includes("regular")) regularizationInsights += 1;
  }

  const topAbsentDepts = Object.entries(deptAbsent)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  const topPresentDepts = Object.entries(deptPresent)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  const topRegDepts = Object.entries(deptRegularization)
    .filter(([, v]) => v > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  const leaveRows = Object.entries(leaveBreakdown)
    .sort((a, b) => b[1] - a[1])
    .map(([code, count]) => ({ code, count }));

  const topFullAttendance = [...employeeScores]
    .filter((e) => e.presentDays > 0)
    .sort((a, b) => {
      if (b.attendanceRate !== a.attendanceRate) {
        return b.attendanceRate - a.attendanceRate;
      }
      return b.presentDays - a.presentDays;
    })
    .slice(0, 5)
    .map((e) => ({
      name: e.empCode ? `${e.name} (${e.empCode})` : e.name,
      count: `${e.attendanceRate}%`,
      sub: `${e.presentDays} present · ${e.absentDays} absent`,
    }));

  const highestAbsentEmployees = [...employeeScores]
    .filter((e) => e.absentDays > 0)
    .sort((a, b) => b.absentDays - a.absentDays)
    .slice(0, 5)
    .map((e) => ({
      name: e.empCode ? `${e.name} (${e.empCode})` : e.name,
      count: e.absentDays,
    }));

  const trackedDays = totalPresentDays + totalAbsentDays + totalLeaveDays;
  const absenteeismRate =
    trackedDays > 0 ? ((totalAbsentDays / trackedDays) * 100).toFixed(1) : "0.0";
  const presentRate =
    trackedDays > 0 ? ((totalPresentDays / trackedDays) * 100).toFixed(1) : "0.0";

  const avgPresentPerEmployee =
    employeesWithData > 0
      ? (totalPresentDays / employeesWithData).toFixed(1)
      : "0.0";

  const dataScopeNote =
    periodMode === "year"
      ? "Year view uses the month currently loaded in the attendance grid — change month in the list to explore other months."
      : periodMode === "range"
      ? "Custom range uses the loaded month’s day-level data."
      : null;

  return {
    periodLabel: periodLabel || monthLabel,
    monthLabel,
    headcount: rows.length,
    employeesWithData,
    daysInMonth,
    totalPresentDays,
    totalAbsentDays,
    totalLeaveDays,
    totalWoDays,
    totalPhDays,
    absenteeismRate,
    presentRate,
    avgPresentPerEmployee,
    pendingLeaveRequests,
    approvedLeaveRequests,
    rejectedLeaveRequests,
    regularizationChecks:
      regularizationInsights +
      Object.values(deptRegularization).reduce((a, b) => a + b, 0),
    topAbsentDepts,
    topPresentDepts,
    topRegDepts,
    leaveRows,
    topFullAttendance,
    highestAbsentEmployees,
    dataScopeNote,
  };
}
